import {FC, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import axios from 'axios'
import toaster from '../../../../Utils/toaster'

const API_URL = process.env.REACT_APP_API_URL

type OutreachRow = {
  interpreterId: string
  interpreterName: string
  bucket: number
  bucketRank?: number | null
  marginPercent?: number | null
  sentAtUtc?: string | null
  sendOutcome: string
  sendFailureReason?: string | null
  response?: string | null
  responseAtUtc?: string | null
  respondedChannel?: string | null
  exclusionReason?: string | null
}

type ConfirmationAttempt = {
  attemptNumber: number
  interpreterId: string
  interpreterName: string
  marginPercent?: number | null
  sentAtUtc?: string | null
  sendOutcome?: string | null
  sendFailureReason?: string | null
  response?: string | null
  responseAtUtc?: string | null
}

type ActivityResponse = {
  hasRun: boolean
  state?: string | null
  outcome?: string | null
  windowOpenedAtUtc?: string | null
  windowClosesAtUtc?: string | null
  cascadeHaltedAtUtc?: string | null
  cascadeHaltReason?: string | null
  eligibleCandidateCount: number
  qualifiedCandidateCount: number
  selectedInterpreterId?: string | null
  selectedInterpreterName?: string | null
  selectionDecidedAtUtc?: string | null
  autoAssignEnabled: boolean
  outreach: OutreachRow[]
  notContactedCount: number
  confirmationAttempts?: ConfirmationAttempt[] | null
}

type Props = {
  appointmentId: number
  onClose: () => void
}

type Badge = {label: string; color: string; bg: string}

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '—'
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatMargin = (value?: number | null) =>
  value == null ? '—' : `${Number(value).toFixed(1)}%`

const POSITIVE_TONE = {color: '#0f7c4a', bg: '#e8fff3'}
const NEGATIVE_TONE = {color: '#c4183c', bg: '#fff5f8'}
const NEUTRAL_TONE = {color: '#5e6278', bg: '#f5f8fa'}
const PENDING_TONE = {color: '#9a7500', bg: '#fff8dd'}

const RESPONSE_BADGES: Record<string, Badge> = {
  Accepted: {label: 'Accepted', ...POSITIVE_TONE},
  Confirmed: {label: 'Confirmed', ...POSITIVE_TONE},
  Declined: {label: 'Declined', ...NEGATIVE_TONE},
}

const SEND_OUTCOME_BADGES: Record<string, Badge> = {
  Skipped: {label: 'Not sent', ...NEUTRAL_TONE},
  Failed: {label: 'Send failed', ...NEGATIVE_TONE},
}

const NO_RESPONSE_BADGE: Badge = {label: 'No response', ...PENDING_TONE}

const describeOutcome = (response?: string | null, sendOutcome?: string | null): Badge =>
  (response ? RESPONSE_BADGES[response] : undefined) ??
  (sendOutcome ? SEND_OUTCOME_BADGES[sendOutcome] : undefined) ??
  NO_RESPONSE_BADGE

const isLateAcceptance = (row: OutreachRow, activity: ActivityResponse) => {
  const cutoff = activity.selectionDecidedAtUtc ?? activity.windowClosesAtUtc
  if (row.response !== 'Accepted' || !row.responseAtUtc || !cutoff) return false
  return new Date(row.responseAtUtc).getTime() > new Date(cutoff).getTime()
}

const describeOutreachOutcome = (row: OutreachRow, activity: ActivityResponse): Badge =>
  isLateAcceptance(row, activity)
    ? {label: 'Accepted Late', ...PENDING_TONE}
    : describeOutcome(row.response, row.sendOutcome)

const STATE_LABELS: Record<string, string> = {
  Created: 'Waiting for the interpreter pool',
  AwaitingPool: 'Waiting for the interpreter pool',
  Qualifying: 'Qualifying interpreters',
  Outreach: 'Contacting interpreters',
  Selecting: 'Selecting an interpreter',
  AwaitingConfirmation: 'Awaiting confirmation',
  Recommended: 'Recommendation made',
  Assigned: 'Assigned automatically',
  NoAcceptances: 'Nobody accepted',
  NoQualifiedCandidates: 'No qualified interpreters',
  Abandoned: 'Stopped',
}

const CHANNEL_LABELS: Record<string, string> = {
  Sms: 'SMS',
  Email: 'email',
  Portal: 'the portal',
}

const EXCLUSION_LABELS: Record<string, string> = {
  BelowMargin: 'Below margin threshold',
  Busy: 'Already booked at this time',
  TimeOff: 'On approved time off',
  NoRate: 'No usable interpreter rate',
  NoPhone: 'No usable mobile number',
  Unreliable: 'Above the no-show threshold',
  NotReached: 'Cascade did not reach them',
}

const SectionHeader: FC<{
  title: string
  isOpen: boolean
  onToggle: () => void
  controls: string
}> = ({title, isOpen, onToggle, controls}) => (
  <button
    type='button'
    className='d-flex align-items-center justify-content-between w-100 border-0 bg-transparent text-start px-0 py-3 mb-2'
    onClick={onToggle}
    aria-expanded={isOpen}
    aria-controls={controls}
  >
    <span
      className='fw-bold text-uppercase text-gray-800'
      style={{fontSize: '0.8rem', letterSpacing: '0.08em'}}
    >
      {title}
    </span>
    <i
      className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-gray-600 fs-7`}
      aria-hidden='true'
    />
  </button>
)

const OUTREACH_COLUMN_WIDTHS = ['6%', '24%', '8%', '10%', '16%', '19%', '17%']
const CONFIRMATION_COLUMN_WIDTHS = ['6%', '32%', '10%', '16%', '19%', '17%']

const ColumnWidths: FC<{widths: string[]}> = ({widths}) => (
  <colgroup>
    {widths.map((width, index) => (
      <col key={index} style={{width}} />
    ))}
  </colgroup>
)

const SmsActivityModal: FC<Props> = ({appointmentId, onClose}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [activity, setActivity] = useState<ActivityResponse | null>(null)
  const [isOutreachOpen, setIsOutreachOpen] = useState(true)
  const [isRotationOpen, setIsRotationOpen] = useState(true)

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    const fetchActivity = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get<{result: ActivityResponse}>(
          `${API_URL}/Appoinment/${appointmentId}/ai-assignment/activity`
        )
        setActivity(response.data?.result ?? null)
      } catch (error) {
        console.error('Error fetching AI assignment activity:', error)
        toaster('error', 'Failed to load SMS activity')
      } finally {
        setIsLoading(false)
      }
    }

    fetchActivity()
  }, [appointmentId])

  return createPortal(
    <>
      <div className='modal fade show d-block' tabIndex={-1} role='dialog'>
        <div className='modal-dialog modal-dialog-centered modal-xl' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>SMS Activities</h5>
              <button type='button' className='btn-close' onClick={onClose} aria-label='Close' />
            </div>

            <div className='modal-body'>
              {isLoading && (
                <div className='text-center py-10'>
                  <span className='spinner-border spinner-border-sm me-2' />
                  Loading outreach history...
                </div>
              )}

              {!isLoading && !activity?.hasRun && (
                <div className='text-muted py-6'>
                  The AI Assignment Agent did not handle this appointment, so there is no outreach
                  history to show.
                </div>
              )}

              {!isLoading && activity?.hasRun && (
                <>
                  <div
                    className='d-flex flex-wrap'
                    style={{gap: 24, marginBottom: 20, fontSize: '0.9rem'}}
                  >
                    <div>
                      <div className='text-muted fs-8'>Status</div>
                      <div className='fw-bold'>
                        {(activity.state && STATE_LABELS[activity.state]) ?? activity.state}
                      </div>
                    </div>
                    <div>
                      <div className='text-muted fs-8'>Pool</div>
                      <div className='fw-bold'>
                        {activity.qualifiedCandidateCount} qualified of{' '}
                        {activity.eligibleCandidateCount} eligible
                      </div>
                    </div>
                    <div>
                      <div className='text-muted fs-8'>Outreach window</div>
                      <div className='fw-bold'>
                        {formatDateTime(activity.windowOpenedAtUtc)} to{' '}
                        {formatDateTime(activity.windowClosesAtUtc)}
                      </div>
                    </div>
                    {activity.selectedInterpreterName && (
                      <div>
                        <div className='text-muted fs-8'>Selected</div>
                        <div className='fw-bold'>{activity.selectedInterpreterName}</div>
                        {activity.selectionDecidedAtUtc && (
                          <div className='text-muted fs-8'>
                            Decided {formatDateTime(activity.selectionDecidedAtUtc)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <SectionHeader
                    title='Outreach Activity'
                    isOpen={isOutreachOpen}
                    onToggle={() => setIsOutreachOpen((open) => !open)}
                    controls='ai-outreach-section'
                  />

                  {isOutreachOpen && (
                    <div id='ai-outreach-section'>
                      {activity.outreach.length === 0 ? (
                        <div className='text-muted fs-7 mb-6'>
                          No SMS was sent for this appointment.
                        </div>
                      ) : (
                        <div className='table-responsive mb-4'>
                          <table className='table table-row-bordered align-top fs-7 mb-0'>
                            <ColumnWidths widths={OUTREACH_COLUMN_WIDTHS} />
                            <thead>
                              <tr className='fw-bold text-muted text-start'>
                                <th>S#</th>
                                <th>Interpreter</th>
                                <th>Bucket</th>
                                <th>Margin</th>
                                <th>SMS Sent</th>
                                <th>Response</th>
                                <th>Responded</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activity.outreach.map((row, index) => {
                                const outcome = describeOutreachOutcome(row, activity)
                                return (
                                  <tr key={`${row.interpreterId}-${row.bucket}`}>
                                    <td className='text-muted'>{index + 1}</td>
                                    <td className='fw-semibold'>{row.interpreterName}</td>
                                    <td>{row.bucket}</td>
                                    <td>{formatMargin(row.marginPercent)}</td>
                                    <td>{formatDateTime(row.sentAtUtc)}</td>
                                    <td>
                                      <span
                                        className='badge'
                                        style={{background: outcome.bg, color: outcome.color}}
                                      >
                                        {outcome.label}
                                      </span>
                                      {row.exclusionReason && (
                                        <div className='text-muted fs-8 mt-1'>
                                          {EXCLUSION_LABELS[row.exclusionReason] ??
                                            row.exclusionReason}
                                        </div>
                                      )}
                                      {row.sendFailureReason && (
                                        <div className='text-muted fs-8 mt-1'>
                                          {row.sendFailureReason}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      {formatDateTime(row.responseAtUtc)}
                                      {row.respondedChannel && (
                                        <div className='text-muted fs-8'>
                                          via{' '}
                                          {CHANNEL_LABELS[row.respondedChannel] ??
                                            row.respondedChannel}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {activity.notContactedCount > 0 && (
                        <div className='text-muted fs-7 mb-6'>
                          {activity.cascadeHaltedAtUtc
                            ? `The cascade stopped early, so ${activity.notContactedCount} qualified interpreter(s) in later buckets were never contacted.`
                            : `${activity.notContactedCount} qualified interpreter(s) were never contacted because the outreach window closed first.`}
                          {activity.cascadeHaltReason && ` ${activity.cascadeHaltReason}`}
                        </div>
                      )}
                    </div>
                  )}

                  {!!activity.confirmationAttempts?.length && (
                    <>
                      <SectionHeader
                        title='Assignment Confirmation'
                        isOpen={isRotationOpen}
                        onToggle={() => setIsRotationOpen((open) => !open)}
                        controls='ai-confirmation-section'
                      />

                      {isRotationOpen && (
                        <div id='ai-confirmation-section'>
                          <div className='text-muted fs-7 mb-3'>
                            Before assigning, the agent asked each interpreter in turn to confirm
                            they would take the job, in order of profit margin.
                          </div>

                          <div className='table-responsive mb-6'>
                            <table className='table table-row-bordered align-top fs-7 mb-0'>
                              <ColumnWidths widths={CONFIRMATION_COLUMN_WIDTHS} />
                              <thead>
                                <tr className='fw-bold text-muted text-start'>
                                  <th>S#</th>
                                  <th>Interpreter</th>
                                  <th>Margin</th>
                                  <th>SMS Sent</th>
                                  <th>Response</th>
                                  <th>Responded</th>
                                </tr>
                              </thead>
                              <tbody>
                                {activity.confirmationAttempts.map((attempt) => {
                                  const outcome = describeOutcome(
                                    attempt.response,
                                    attempt.sendOutcome
                                  )
                                  return (
                                    <tr key={`${attempt.attemptNumber}-${attempt.interpreterId}`}>
                                      <td className='text-muted'>{attempt.attemptNumber}</td>
                                      <td className='fw-semibold'>{attempt.interpreterName}</td>
                                      <td>{formatMargin(attempt.marginPercent)}</td>
                                      <td>{formatDateTime(attempt.sentAtUtc)}</td>
                                      <td>
                                        <span
                                          className='badge'
                                          style={{background: outcome.bg, color: outcome.color}}
                                        >
                                          {outcome.label}
                                        </span>
                                        {attempt.sendFailureReason && (
                                          <div className='text-muted fs-8 mt-1'>
                                            {attempt.sendFailureReason}
                                          </div>
                                        )}
                                      </td>
                                      <td>{formatDateTime(attempt.responseAtUtc)}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' />
    </>,
    document.body
  )
}

export default SmsActivityModal
